import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
	Document,
	DocumentsSharePayload,
	SharedDocument,
	CreateDocumentRequest,
	UpdateDocumentRequest,
	sharedRecord,
	ShareDestination,
	ShareDestinationsResponse
} from '../../types/document';
import { AdminShareOverviewItem, ShareHistoryItem, ShareHistoryPage } from '../../types/sharing';
import { API } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';
import { enqueueSnackbar } from 'notistack';
import { RequestStatusObject, buildReducers, createRequestStatusObject } from '../utils';

export interface DocumentState {
	userDocuments?: Document[];
	proxyUserDocuments?: Document[];
	proxyUserId?: string;
	userBulkUploadDocuments?: Document[];
	pageDocuments?: Document[];
	currentDocument?: Document;
	selectedDocuments: Document[];
	sharedDocuments?: SharedDocument[];
	sharedRecord?: sharedRecord;
	listDocumentsRequest: RequestStatusObject;
	listProxyDocumentsRequest: RequestStatusObject;
	listBulkUploadDocumentsRequest: RequestStatusObject;
	readDocumentRequest: RequestStatusObject;
	updateDocumentRequest: RequestStatusObject;
	createDocumentRequest: RequestStatusObject;
	deleteDocumentRequest: RequestStatusObject;
	bulkUploadDocumentRequest: RequestStatusObject; // Added new request status object
	deleteBulkUploadDocumentRequest: RequestStatusObject; // Added new request status object
	getPresignedUploadUrlRequest: RequestStatusObject;
	uploadFileToS3Request: RequestStatusObject;
	shareDestinations: ShareDestination[];
	shareDestinationsLoading: boolean;
	// True when the cached partner list could not be refreshed; search still works,
	// the UI just hints that the list may be out of date.
	shareDestinationsStale: boolean;
	shareDestinationsError: boolean;
	isLoading: boolean;
}

const initialState: DocumentState = {
	userDocuments: undefined,
	proxyUserDocuments: undefined,
	proxyUserId: undefined,
	userBulkUploadDocuments: undefined,
	currentDocument: undefined,
	selectedDocuments: [],
	sharedDocuments: undefined,
	sharedRecord: undefined,
	listDocumentsRequest: createRequestStatusObject(),
	listProxyDocumentsRequest: createRequestStatusObject(),
	listBulkUploadDocumentsRequest: createRequestStatusObject(),
	readDocumentRequest: createRequestStatusObject(),
	updateDocumentRequest: createRequestStatusObject(),
	createDocumentRequest: createRequestStatusObject(),
	deleteDocumentRequest: createRequestStatusObject(),
	bulkUploadDocumentRequest: createRequestStatusObject(), // Added initial state for new request status object
	deleteBulkUploadDocumentRequest: createRequestStatusObject(), // Added initial state for new request status object
	getPresignedUploadUrlRequest: createRequestStatusObject(),
	uploadFileToS3Request: createRequestStatusObject(),
	shareDestinations: [],
	shareDestinationsLoading: false,
	shareDestinationsStale: false,
	shareDestinationsError: false,
	isLoading: false
};

export const listDocuments = createAsyncThunk<Document[], undefined, { dispatch: AppDispatch; state: RootState }>(
	'documents/list',
	async () => {
		const result = await API.get('inspirusapi', '/documents', {
			headers: {},
			response: true,
			queryStringParameters: {}
		});
		return result.data;
	}
);

export const listProxyDocuments = createAsyncThunk<
	Document[],
	{ userId: string },
	{ dispatch: AppDispatch; state: RootState }
>('documents/listProxy', async ({ userId }) => {
	const result = await API.get('inspirusapi', '/documents', {
		headers: {},
		response: true,
		queryStringParameters: { userId }
	});
	return result.data;
});

export const listBulkUploadDocuments = createAsyncThunk<
	Document[],
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>('documents/listBulkUploadDocuments', async () => {
	const result = await API.get('inspirusapi', '/documents/bulk-upload-documents', {
		headers: {},
		response: true,
		queryStringParameters: {}
	});
	return result.data;
});

export const readDocument = createAsyncThunk<Document, string, { dispatch: AppDispatch; state: RootState }>(
	'documents/read',
	async (documentId) => {
		const result = await API.get('inspirusapi', `/documents/${documentId}`, {
			headers: {},
			response: true,
			queryStringParameters: {}
		});
		if (!result.data[0]) {
			throw new Error('not found');
		}
		return result.data[0] as Document;
	}
);

export const getDocuments = createAsyncThunk<Document[], string[], { dispatch: AppDispatch; state: RootState }>(
	'documents/getByIds',
	async (documentIds) => {
		const promises = documentIds.map((docId) =>
			API.get('inspirusapi', `/documents/${docId}`, {
				headers: {},
				response: true,
				queryStringParameters: {}
			})
		);
		const responses = await Promise.all(promises);

		const documents = responses.map((res) => res.data[0]).filter((v) => !!v);
		return documents as Document[];
	}
);

export const createDocument = createAsyncThunk<any, CreateDocumentRequest, { dispatch: AppDispatch; state: RootState }>(
	'documents/create',
	async (payload) => {
		try {
			const result = await API.post('inspirusapi', '/documents', {
				headers: {},
				response: true,
				body: payload
			});

			let blockchainStatusResponseMessage;
			const blockchainData = result.data.blockchain_response.data;
			const blockchainIdOrTx = blockchainData.txId || blockchainData.blockchainId;

			if (blockchainIdOrTx) {
				blockchainStatusResponseMessage = `${result.data.blockchain_response.body} - Blockchain ID: \n${blockchainIdOrTx}`;
				enqueueSnackbar(blockchainStatusResponseMessage, { variant: 'success' });
			} else {
				blockchainStatusResponseMessage = blockchainData;
				enqueueSnackbar(blockchainStatusResponseMessage, { variant: 'error' });
			}

			return result.data.item;
		} catch (error) {
			console.error('Error creating document:', error);
		}
	}
);

export const updateDocument = createAsyncThunk<any, UpdateDocumentRequest, { dispatch: AppDispatch; state: RootState }>(
	'documents/update',
	async (payload) => {
		const result = await API.put('inspirusapi', `/documents/${payload.id}`, {
			headers: {},
			response: true,
			body: payload
		});
		return result.data.Attributes;
	}
);

export const shareDocuments = createAsyncThunk<any, DocumentsSharePayload, { dispatch: AppDispatch; state: RootState }>(
	'documents/share',
	async ({ receivers, documents, senderName, deliveryMode, destinationUuids }, { getState }) => {
		if (!documents) {
			documents = getState().document.selectedDocuments ?? [];
		}
		const extraArgs: { [key: string]: string } = {};

		const currentUser = getState().user.currentUser;

		const isInstitutionShare = deliveryMode === 'institution';

		const files = documents.map(
			({
				title,
				id,
				path,
				verification,
				subcategory,
				partnerUploaded,
				organisationId,
				externalReference
			}: Document) => ({
				title,
				id,
				path,
				verification: verification ?? undefined,
				// Institution shares are resolved server-side from `id` alone; these
				// travel only so a not-yet-updated backend still has what it expects.
				// Safe to drop once the re-reading ssharing is live everywhere.
				...(isInstitutionShare ? { subcategory, partnerUploaded, organisationId, externalReference } : {})
			})
		);
		const documentTitles = documents.map(({ title }: Document) => title).filter(Boolean);
		const result = await API.post('inspirusapi', '/sharing', {
			headers: {},
			response: true,
			body: {
				files,
				receivers,
				senderName,
				senderId: currentUser?.id,
				senderEmail: currentUser?.email,
				documentTitles,
				// Absent for an email share, keeping that request identical to before.
				...(isInstitutionShare ? { deliveryMode, destinationUuids } : {}),
				...extraArgs
			}
		});
		return result.data;
	}
);

/**
 * Institution search for the share dialog. Hits our cached copy of the partner
 * network — never the partner directly — and receives a projection with contact
 * addresses already stripped.
 */
export const fetchShareDestinations = createAsyncThunk<
	ShareDestinationsResponse,
	{ q: string; limit?: number },
	{ dispatch: AppDispatch; state: RootState }
>('documents/fetchShareDestinations', async ({ q, limit }) => {
	return (await API.get('inspirusapi', '/share-delivery/destinations', {
		queryStringParameters: { q, ...(limit ? { limit } : {}) }
	})) as ShareDestinationsResponse;
});

export const getSharedDocuments = createAsyncThunk<
	any,
	string | { sharingId: string; token?: string },
	{ dispatch: AppDispatch; state: RootState; rejectValue: { status?: number } }
>('documents/getSharedDocuments', async (arg, { rejectWithValue }) => {
	const sharingId = typeof arg === 'string' ? arg : arg.sharingId;
	const token = typeof arg === 'string' ? undefined : arg.token;
	try {
		return await API.get('inspirusapi', '/sharing/' + sharingId, {
			...(token ? { queryStringParameters: { r: token } } : {})
		});
	} catch (error: any) {
		// Kept as a value: a thrown error is serialised without its response, and the page
		// needs the status to tell a withdrawn share (410) from a broken link.
		return rejectWithValue({ status: error?.response?.status });
	}
});

/**
 * One page of the signed-in user's share history, newest first. Pass the previous page's
 * `nextCursor` to load the next; `nextCursor` is null on the last page.
 */
export const getShareHistory = createAsyncThunk<
	ShareHistoryPage<ShareHistoryItem>,
	{ cursor?: string | null } | undefined,
	{ dispatch: AppDispatch; state: RootState }
>('documents/getShareHistory', async (arg) =>
	API.get('inspirusapi', '/sharing/history', {
		queryStringParameters: { ...(arg?.cursor ? { cursor: arg.cursor } : {}) }
	})
);

/**
 * One page of every share on the platform, for App Admins. Filters apply across all shares,
 * not just the page: a filtered page may come back short (or empty) with a `nextCursor` while
 * the search continues.
 */
export const getAdminShareOverview = createAsyncThunk<
	ShareHistoryPage<AdminShareOverviewItem>,
	{ sender?: string; email?: string; cursor?: string | null },
	{ dispatch: AppDispatch; state: RootState }
>('documents/getAdminShareOverview', async ({ sender, email, cursor }) =>
	API.get('inspirusapi', '/sharing/admin/overview', {
		queryStringParameters: {
			...(sender?.trim() ? { sender: sender.trim() } : {}),
			...(email?.trim() ? { email: email.trim() } : {}),
			...(cursor ? { cursor } : {})
		}
	})
);

export const deleteDocument = createAsyncThunk<
	any,
	{ documentId: string; verificationId?: string; verificationStatus?: string },
	{ dispatch: AppDispatch; state: RootState }
>('documents/delete', async ({ documentId, verificationId, verificationStatus }) => {
	const result = await API.del('inspirusapi', '/documents', {
		headers: {},
		response: true,
		body: {
			documentId,
			verificationId,
			verificationStatus
		}
	});
	return result.data;
});

// Add bulkUploadDocument thunk
export const bulkUploadDocument = createAsyncThunk<
	Document,
	{ documentId: string; payload: UpdateDocumentRequest },
	{ dispatch: AppDispatch; state: RootState }
>('documents/bulkUploadDocument', async ({ documentId, payload }) => {
	const result = await API.put('inspirusapi', `/documents/bulk-upload-documents/${documentId}`, {
		headers: {},
		response: true,
		body: payload
	});
	return result.data; // Make sure this contains the updated document including the id
});

// Add deleteBulkUploadDocument thunk
export const deleteBulkUploadDocument = createAsyncThunk<
	{ documentId: string },
	{ documentId: string },
	{ dispatch: AppDispatch; state: RootState }
>('documents/deleteBulkUploadDocument', async ({ documentId }) => {
	await API.del('inspirusapi', `/documents/bulk-upload-documents/${documentId}`, {
		headers: {},
		response: true
	});
	return { documentId };
});

export const getPresignedUploadUrl = createAsyncThunk<
	{ uploadUrl: string; key: string; identityId: string; fileHash: string },
	{ targetUserId: string; filename: string; contentType: string },
	{ dispatch: AppDispatch; state: RootState }
>('documents/getPresignedUploadUrl', async ({ targetUserId, filename, contentType }) => {
	const result = await API.post('inspirusapi', '/documents/presigned-upload', {
		headers: {},
		response: true,
		body: {
			targetUserId,
			filename,
			contentType
		}
	});
	return result.data;
});

export const uploadFileToS3 = createAsyncThunk<
	void,
	{ uploadUrl: string; file: File },
	{ dispatch: AppDispatch; state: RootState }
>('documents/uploadFileToS3', async ({ uploadUrl, file }) => {
	await fetch(uploadUrl, {
		method: 'PUT',
		body: file,
		headers: {
			'Content-Type': file.type
		}
	});
});

const DocumentsSlice = createSlice({
	name: 'Documents',
	initialState,
	reducers: {
		resetDocuments(state) {
			state = initialState;
			return state;
		},
		resetCurrentDocument(state) {
			state.currentDocument = undefined;
			return state;
		},
		selectDocuments(state, action) {
			state.selectedDocuments = action.payload ?? [];
			return state;
		},
		toggleSelection(state, action) {
			const document = action.payload as Document;
			const selection = state.selectedDocuments ?? [];
			if (selection?.find((selectedDocument) => selectedDocument.id === document.id)) {
				state.selectedDocuments = selection.filter((selectedDocument) => selectedDocument.id !== document.id);
			} else {
				selection.push(document);
				state.selectedDocuments = selection;
			}
			return state;
		},
		clearSelection(state) {
			state.selectedDocuments = [];
			return state;
		},
		updateCurrentDocument(state, action) {
			const document = { ...state.currentDocument, ...action.payload };
			state.currentDocument = document;
			state.userDocuments = [document, ...(state.userDocuments?.filter((doc) => doc.id !== document.id) ?? [])];
		},
		clearProxyDocuments(state) {
			state.proxyUserDocuments = undefined;
			state.proxyUserId = undefined;
			state.listProxyDocumentsRequest = createRequestStatusObject();
		}
	},
	extraReducers: (builder) => {
		builder.addCase(fetchShareDestinations.pending, (state) => {
			state.shareDestinationsLoading = true;
			state.shareDestinationsError = false;
		});
		builder.addCase(fetchShareDestinations.fulfilled, (state, action) => {
			state.shareDestinationsLoading = false;
			state.shareDestinations = action.payload.destinations ?? [];
			state.shareDestinationsStale = action.payload.stale === true;
		});
		builder.addCase(fetchShareDestinations.rejected, (state) => {
			state.shareDestinationsLoading = false;
			state.shareDestinationsError = true;
			state.shareDestinations = [];
		});
		buildReducers(builder, listDocuments, 'listDocumentsRequest', {
			onFulfilled: (state, payload) => {
				state.userDocuments = payload;
			}
		});
		buildReducers(builder, listProxyDocuments, 'listProxyDocumentsRequest', {
			onFulfilled: (state, payload) => {
				state.proxyUserDocuments = payload;
			}
		});
		buildReducers(builder, listBulkUploadDocuments, 'listBulkUploadDocumentsRequest', {
			onFulfilled: (state, payload) => {
				state.userBulkUploadDocuments = payload;
			}
		});
		buildReducers(builder, getDocuments, 'listDocumentsRequest', {
			onPending: (state) => {
				state.pageDocuments = [];
			},
			onFulfilled: (state, payload) => {
				state.pageDocuments = payload;
			}
		});
		buildReducers(builder, readDocument, 'readDocumentRequest', {
			onFulfilled: (state, payload: Document) => {
				state.currentDocument = payload;
				state.isLoading = false;
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state) => {
				state.isLoading = false;
			}
		});
		buildReducers(builder, createDocument, 'createDocumentRequest', {
			onPending: (state) => {
				state.isLoading = true;
			},
			onFulfilled: (state, payload) => {
				state.currentDocument = payload;
				state.userDocuments = [...(state.userDocuments ?? []), payload];
				state.isLoading = false;
			}
		});
		buildReducers(builder, updateDocument, 'updateDocumentRequest', {
			onFulfilled: (state, payload) => {
				const doc = { ...state.currentDocument, ...payload };
				state.currentDocument = doc;
				state.userDocuments = [
					doc,
					...(state.userDocuments?.filter((doc: Document) => doc.id !== payload.id) ?? [])
				];
			}
		});
		buildReducers(builder, deleteDocument, 'deleteDocumentRequest', {
			onFulfilled: (state, payload) => {
				enqueueSnackbar('Document deleted successfully', {
					variant: 'success',
					persist: false
				});
				state.userDocuments = state.userDocuments?.filter((doc: Document) => doc.id !== payload.key);
			}
		});

		builder.addCase(getSharedDocuments.fulfilled, (state, action) => {
			state.sharedRecord = action.payload;
		});

		// Add case for bulkUploadDocument
		buildReducers(builder, bulkUploadDocument, 'bulkUploadDocumentRequest', {
			onFulfilled: (state, payload) => {
				const doc = { ...state.currentDocument, ...payload };
				state.currentDocument = doc;
				state.userBulkUploadDocuments = [
					doc,
					...(state.userBulkUploadDocuments?.filter((doc: Document) => doc.id !== payload.id) ?? [])
				];
			}
		});

		buildReducers(builder, getPresignedUploadUrl, 'getPresignedUploadUrlRequest', {});
		buildReducers(builder, uploadFileToS3, 'uploadFileToS3Request', {});

		// Add case for deleteBulkUploadDocument
		builder.addCase(deleteBulkUploadDocument.fulfilled, (state, action) => {
			const { documentId } = action.payload;
			enqueueSnackbar('Bulk upload document deleted successfully', {
				variant: 'success',
				persist: false
			});
			state.userBulkUploadDocuments = state.userBulkUploadDocuments?.filter(
				(doc: Document) => doc.id !== documentId
			);
		});
	}
});

export const {
	resetDocuments,
	resetCurrentDocument,
	toggleSelection,
	clearSelection,
	selectDocuments,
	updateCurrentDocument,
	clearProxyDocuments
} = DocumentsSlice.actions;

export default DocumentsSlice.reducer;
